/**
 * Calculate the distance between two coordinates using the Haversine formula
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers
  return Math.round(distance); // Round to nearest kilometer
}

/**
 * Format distance for display
 * @param distance Distance in kilometers
 * @param useMiles Whether to display in miles (for US) or kilometers (for Canada)
 * @returns Formatted distance string
 */
export function formatDistance(distance: number, useMiles: boolean = false): string {
  if (useMiles) {
    // Convert kilometers to miles (1 km = 0.621371 miles)
    const miles = distance * 0.621371;
    if (miles === 0) return '0 mi';
    if (miles < 1) return '< 1 mi';
    return `${Math.round(miles).toLocaleString()} mi`;
  } else {
    // Display in kilometers (default for Canada)
    if (distance === 0) return '0 km';
    if (distance < 1) return '< 1 km';
    return `${distance.toLocaleString()} km`;
  }
}
